import type { HarnessProgress } from '~/components/pages/domain/components/domainCard';
import { toolboxApi, unwrapResult, type ApiEnvelope } from '~/modules/query/base';

/** GET /harnesses.domainSetup.{siteType}/NEW — the harness skeleton the progress UI renders. */
export type DomainSkeleton = HarnessProgress;

/** GET /harnesses.domainSetup.{siteType}/{domain}/progress */
export type DomainProgress = HarnessProgress;

export interface DomainProgressArgs {
  siteType: string;
  domain: string;
}

export interface DomainSetupArgs {
  domain: string;
  siteType: string;
  raze?: boolean;
}

/** POST /harnesses.domainSetup.{siteType} — callers read `status`. */
export type DomainSetupResponse = ApiEnvelope<unknown>;

export const domainApi = toolboxApi.injectEndpoints({
  endpoints: (builder) => ({
    getSiteType: builder.query<DomainSkeleton, string>({
      query: (siteType) => ({ url: `/harnesses.domainSetup.${siteType}/NEW`, method: 'GET' }),
      transformResponse: unwrapResult<DomainSkeleton>,
      providesTags: ['DomainSetup'],
    }),

    getDomainProgress: builder.query<DomainProgress, DomainProgressArgs>({
      query: ({ siteType, domain }) => ({
        url: `/harnesses.domainSetup.${siteType}/${encodeURIComponent(domain)}/progress`,
        method: 'GET',
      }),
      transformResponse: unwrapResult<DomainProgress>,
      providesTags: ['DomainSetup'],
    }),

    // The envelope is kept because the page reads `status` to tell a started job from a refused one.
    startDomainSetup: builder.mutation<DomainSetupResponse, DomainSetupArgs>({
      query: ({ domain, siteType, raze = false }) => ({
        url: `/harnesses.domainSetup.${siteType}?runAsync=t${raze ? '&raze=t' : ''}`,
        method: 'POST',
        data: { domain },
      }),
      invalidatesTags: ['DomainSetup'],
    }),
  }),
});

export const {
  useGetSiteTypeQuery,
  useLazyGetSiteTypeQuery,
  useGetDomainProgressQuery,
  useStartDomainSetupMutation,
} = domainApi;
